import React, {
  CSSProperties,
  FunctionComponent,
  useEffect,
  useState,
} from 'react';
import {useDispatch} from 'react-redux';
import swal from 'sweetalert2';
import Dropzone from 'react-dropzone';
import ReactMde from 'react-mde';
import * as Showdown from 'showdown';
import { getEndpoint, getFantasyEndpoint, getHeaders } from '../../../../helpers/endpoint';
import { InputLabelEditor } from '../../../../components/InputLabelEditor';
import { ChampionshipSelector } from '../../../../components/ChampionshipSelector';

const converter = new Showdown.Converter({
  tables: true,
  simplifiedAutoLink: true,
  strikethrough: true,
  tasklists: true,
});

const getChampionship = (
  championshipId: number,
  onSuccess: (response: any) => void,
) => {
  return (_dispatch, getState) => {
    return fetch(getEndpoint('getMXIChampionshipCalendar', championshipId), {
      method: 'GET',
      headers: getHeaders(getState().authentication),
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
        } else {
          onSuccess(data);
        }
      })
      .catch(() => {});
  };
};

const updatePrize = (
  championshipId: number | undefined,
  gameWeekNumber: number | undefined,
  payload: any,
) => {
  return (_dispatch, getState) => {
    return fetch(getFantasyEndpoint('updatePrize', {championshipId, gameWeekNumber}), {
      method: 'PATCH',
      headers: getHeaders(getState().authentication),
      body: JSON.stringify(payload),
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
          swal({
            title: `Oops...`,
            type: 'error',
            html: `Something went wrong !<br/>${data.message}<br/>`,
          });
        } else {
          swal({
            title: `Updated !`,
            type: 'success',
          });
        }
      })
      .catch(() => {});
  };
};

export const postImageUpload = (
  file: File,
  onSuccess: (imageUrl: string) => void,
) => {
  const formData = new FormData();
  formData.append('file', file);
  return (_dispatch, getState) => {
    fetch(getEndpoint('postImageUpload'), {
      method: 'POST',
      headers: {Authorization: getState().authentication.token},
      body: formData,
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
          swal({
            title: `Oops...`,
            type: 'error',
            html: `Something went wrong with: "${data.error}"<br/>`,
          });
        } else {
          onSuccess(data.url);
        }
      })
      .catch(error => {
        swal({
          title: `Oops...`,
          type: 'error',
          html: `Something went wrong with: "${error}"<br/>`,
        });
      });
  };
};

export const languagesFlagsRefs = {
  'fr-FR': 'fr',
  'en-GB': 'gb-eng',
  'es-ES': 'es',
};

export const LanguageSelector = ({
  onChange,
  selectedLanguage,
}: {
  onChange: (language: string) => void;
  selectedLanguage: string;
}) => {
  return (
    <div style={styles.flagsContainer}>
      {Object.keys(languagesFlagsRefs).map(language => (
        <div onClick={() => onChange(language)}>
          <span
            style={{
              ...styles.flag,
              ...(selectedLanguage === language && styles.selectedFlag),
            }}
            key={language}
            className={`flag-icon flag-icon-${languagesFlagsRefs[language]} flag-icon-squared`}
          />
          {selectedLanguage === language && (
            <div
              style={{
                width: 40,
                backgroundColor: '#4054CC',
                height: 2,
                marginLeft: 4,
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
};

const Prize: FunctionComponent = () => {
  const dispatch = useDispatch();

  const [selectedLanguage, setSelectedLanguage] = useState<string>('fr-FR');
  const [gameWeeksNumber, setGameWeeksNumber] = useState<string[]>();
  const [selectedGameWeek, setSelectedGameWeek] = useState<number>(1);
  const [championshipId, setChampionshipId] = useState<number>(1);
  
  const [selectedTab, setSelectedTab] = useState<'write' | 'preview'>(
    'write',
  );

  const onGetChampionshipSuccess = (response) => {
    setGameWeeksNumber(Object.keys(response?.gameWeeks || {}));
  }

  useEffect(() => {
    dispatch(getChampionship(1, onGetChampionshipSuccess));
}, [championshipId]);

  const [prize, setPrize] = useState<{
    description?: { [key: string]: string } | null;
    imagesUrls?: { mobile: string | null; desktop: string | null; };
  }>();

  const onSubmit = () => {
    dispatch(updatePrize(championshipId, selectedGameWeek, prize));
  };

  const updatePrizeImage = (platform: string, imageUrl: string | null) => {
    setPrize((old) => ({ 
      ...old, 
      imagesUrls: { 
        mobile: platform === 'mobile' ? imageUrl : old?.imagesUrls?.mobile || null, 
        desktop: platform === 'desktop' ? imageUrl : old?.imagesUrls?.desktop || null 
      }
    }));
  };

  const isDisabled =
    !prize?.description?.['fr-FR']?.length ||
    !prize?.description?.['en-GB']?.length ||
    !prize?.description?.['es-ES']?.length ||
    !prize?.imagesUrls?.mobile ||
    !prize?.imagesUrls?.desktop;

  return (
    <div style={styles.container}>
      <div style={styles.flex1}>
        <div style={styles.titleContainer}>Update prize</div>
        <div style={styles.contentContainer}>
          <style style={{marginBottom: 16}}>
            <ChampionshipSelector
              fromMpp={false}
              championship={championshipId}
              onChange={setChampionshipId}
            />
          </style>
          <InputLabelEditor title='GAME WEEK' isRequired />
          <select
            style={{width: 250, margin: 16, marginLeft: 0}}
            className="form-control"
            onChange={event =>
              setSelectedGameWeek(parseInt(event.target.value, 10))
            }
            value={selectedGameWeek}>
            {gameWeeksNumber?.map(gameWeekNumber => (
              <option value={gameWeekNumber}>{gameWeekNumber}</option>
            ))}
          </select>
          {['mobile', 'desktop'].map(platform => (
            <div style={styles.inputContainer}>
              <InputLabelEditor title={`IMAGE ${platform.toUpperCase()}`} isRequired />
              <div style={{display: 'flex', flexDirection: 'row'}}>
                <Dropzone
                  style={styles.dropZone}
                  onDrop={acceptedFiles => {
                    const onSuccess = (imageUrl: string) => {
                      updatePrizeImage(platform, imageUrl);
                    };
                    dispatch(postImageUpload(acceptedFiles[0], onSuccess));
                  }}>
                  <div>
                    {!prize?.imagesUrls?.[platform] ? (
                      <span>Parcourir ou Drag n Drop</span>
                    ) : (
                      <img
                        src={prize.imagesUrls?.[platform]}
                        style={{maxWidth: 360}}
                        alt={platform}
                      />
                    )}
                  </div>
                </Dropzone>
                {prize?.imagesUrls?.[platform] && (
                  <div
                    onClick={() => {
                        updatePrizeImage(platform, null);
                      }
                    }
                    style={{
                      marginLeft: 16,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: 30,
                      height: 30,
                      backgroundColor: 'red',
                      borderRadius: 30,
                      cursor: 'pointer',
                    }}>
                    <i style={{color: 'white'}} className="fa fa-trash" />
                  </div>
                )}
              </div>
            </div>
          ))}
          <InputLabelEditor title='DESCRIPTION' isRequired />
          <LanguageSelector
            onChange={language => setSelectedLanguage(language)}
            selectedLanguage={selectedLanguage}
          />
          <ReactMde
            toolbarCommands={[
              ['bold', 'italic', 'strikethrough'],
              ['unordered-list', 'ordered-list'],
              ['link'],
            ]}
            value={prize?.description?.[selectedLanguage] || ''}
            onChange={value =>
              setPrize((old) => ({ ...old, description: { ...old?.description, [selectedLanguage]: value } }))
            }
            selectedTab={selectedTab}
            onTabChange={setSelectedTab}
            generateMarkdownPreview={markdown =>
              Promise.resolve(converter.makeHtml(markdown))
            }
          />
        </div>
        <div style={styles.buttonContainer}>
          <button
            disabled={isDisabled}
            style={
              !isDisabled ? styles.saveButton : styles.disabledButton
            }
            onClick={onSubmit}>
            Sauvegarder
          </button>
          <span style={{marginLeft: 8, color: 'red'}}>*</span>
        </div>
      </div>
    </div>
  );
};

export default Prize;

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'row',
    margin: 16,
  },
  contentContainer: {
    padding: 16,
    backgroundColor: 'white',
  },
  titleContainer: {
    display: 'flex',
    height: 43,
    backgroundColor: 'white',
    width: '100%',
    alignItems: 'center',
    fontSize: 18,
    paddingLeft: 16,
    marginBottom: 16,
  },
  flex1: {
    flex: 1,
  },
  saveButton: {
    height: 48,
    width: 150,
    color: '#4054CC',
    borderRadius: 4,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#4054CC',
    fontWeight: 'bold',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 32,
  },
  disabledButton: {
    height: 48,
    width: 150,
    color: 'white',
    borderRadius: 4,
    backgroundColor: '#959DAF',
    fontWeight: 'bold',
    borderWidth: 0,
  },
  flagsContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  flag: {
    margin: 8,
    height: 22,
    width: 22,
    borderRadius: 22,
    cursor: 'pointer',
  },
  selectedFlag: {
    width: 32,
    height: 32,
  },
  inputContainer: {
    marginBottom: 16,
  },
  dropZone: {
    width: 360,
    minHeight: 200,
    border: '1px dashed #4054CC',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#4054CC',
    cursor: 'pointer',
  },
};



// WEBPACK FOOTER //
// ./src/pages/Other/FL1/Prize/index.tsx